/**
 * AI Analysis Service
 *
 * Client-side service for calling the Cloud Function that analyzes check-ins
 * using Gemini 1.5 Flash.
 */

import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { Timestamp } from 'firebase/firestore';
import type { AIAnalysis, CheckIn, NightlyReview } from '../../types/checkin';

// Initialize Firebase Functions
const functions = getFunctions();

// Connect to emulator in development
if (import.meta.env.DEV) {
  connectFunctionsEmulator(functions, 'localhost', 5001);
}

// Type for the Cloud Function input
interface AnalyzeCheckinInput {
  userId: string;
  checkinId: string;
  type: 'morning' | 'nightly';
  responses?: CheckIn['responses'];
  nightlyResponses?: NightlyReview['responses'];
  morningIntentionsShown?: NightlyReview['morningIntentionsShown'];
}

// Type for the Cloud Function response (mirrors functions/src/types.ts AnalyzeCheckinResponse)
export interface AnalyzeCheckinResponse {
  analysis: AIAnalysis | null;
  reason?: 'premium_required';
}

// Initialize the callable function
const analyzeCheckinFunction = httpsCallable<AnalyzeCheckinInput, AnalyzeCheckinResponse>(
  functions,
  'analyzeCheckin'
);

/**
 * Analyze a morning check-in using AI.
 *
 * @param userId - The user's ID (must match authenticated user)
 * @param checkIn - The morning check-in data
 * @returns AnalyzeCheckinResponse — analysis is null when user is not on premium/trial
 * @throws Error if the analysis fails
 */
export async function analyzeMorningCheckIn(
  userId: string,
  checkIn: CheckIn
): Promise<AnalyzeCheckinResponse> {
  try {
    const result = await analyzeCheckinFunction({
      userId,
      checkinId: checkIn.id,
      type: 'morning',
      responses: checkIn.responses,
    });

    return result.data;
  } catch (error) {
    console.error('Error analyzing morning check-in:', error);
    throw new Error(
      `Failed to analyze check-in: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Analyze a nightly review using AI.
 *
 * @param userId - The user's ID (must match authenticated user)
 * @param nightlyReview - The nightly review data (partial, before AI analysis is added)
 * @returns AnalyzeCheckinResponse — analysis is null when user is not on premium/trial
 * @throws Error if the analysis fails
 */
export async function analyzeNightlyReview(
  userId: string,
  nightlyReview: Omit<NightlyReview, 'aiAnalysis'>
): Promise<AnalyzeCheckinResponse> {
  try {
    const result = await analyzeCheckinFunction({
      userId,
      checkinId: nightlyReview.id,
      type: 'nightly',
      nightlyResponses: nightlyReview.responses,
      morningIntentionsShown: nightlyReview.morningIntentionsShown,
    });

    return result.data;
  } catch (error) {
    console.error('Error analyzing nightly review:', error);
    throw new Error(
      `Failed to analyze nightly review: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Check if the AI analysis service is available.
 * Useful for graceful degradation if Cloud Functions are not deployed.
 *
 * @returns true if the service is likely available
 */
export function isAIAnalysisAvailable(): boolean {
  // In production, always return true (function should be deployed)
  if (import.meta.env.PROD) {
    return true;
  }

  // In development, check if emulator is expected to be running
  // This is a simple check - actual availability can only be confirmed by calling the function
  return true;
}

/**
 * Get a fallback AI analysis response in case the Cloud Function is unavailable.
 * Returns the same shape as a successful analysis so callers handle it consistently.
 *
 * @returns AnalyzeCheckinResponse with a basic analysis object
 */
export function getFallbackAnalysis(): AnalyzeCheckinResponse {
  return {
    analysis: {
      feedback: [
        'Thank you for completing your check-in today.',
        'Your responses have been recorded for your reflection.',
      ],
      patterns: [],
      riskLevel: 'low',
      citations: ['Step 10: Daily personal inventory'],
      analyzedAt: Timestamp.now(),
    },
  };
}
